import {computed} from 'mobx';
import {RouterStore} from 'mobx-react-router';
import {isGeneralArray} from '../types/Utility';
import {ParsedQuery, stringify, parse} from 'src/utility/query-string';
import {UnregisterCallback} from 'history';

export enum UrlKey {
    VIEW = 'view',
    FF_MODE = 'ff',
    CHANNEL = 'channel',
    AB_TEST_ID = 'abTestId',
    AB_VARIANT_ID = 'abVariantId',
    INTERVAL = 'interval',
    SEARCH_TERM = 'searchTerm',
    SHOW_COCKPIT_LINKS = 'showCockpitLinks',
}

const turnParamsToHash = (hashParams: ParsedQuery): string => '#' + stringify(hashParams, {});

export interface UrlState {
    setAttributeToUrl: (key: UrlKey, value: string | string[]) => void;
    getAttributeFromHashParams: (key: UrlKey, defaultValue: string) => string;
    getAttributeArrayFromHashParams: (key: UrlKey, defaultValue: string[]) => string[];
    listenToChannelChanges: (onChannelChange: (channelName: string | null) => void) => UnregisterCallback;
}

export default class UrlStore {
    constructor(private routerStore: RouterStore) {}

    private setUrl(hashParams: ParsedQuery) {
        const hash = turnParamsToHash(hashParams);
        this.routerStore.push(hash);

        if (window.parent !== window) {
            delete hashParams[UrlKey.SHOW_COCKPIT_LINKS];
            const reducedHash = turnParamsToHash(hashParams);

            window.parent.postMessage({type: 'urlHash', hash: reducedHash}, '*');
        }
    }

    public setAttributeToUrl(key: UrlKey, value: string | string[]) {
        if ((typeof value === 'string' && value === '') || (Array.isArray(value) && value.length === 0)) {
            return this.removeParamFromUrl(key);
        }
        this.setUrl({
            ...this.hashParams,
            [key]: value,
        });
    }

    private removeParamFromUrl = (key: UrlKey) => {
        const hashParams = this.hashParams;
        delete hashParams[key];
        this.setUrl(hashParams);
    };

    @computed
    private get hashParams(): ParsedQuery {
        return {
            ...parse(
                this.routerStore.location.hash.indexOf('#') === 0 ? this.routerStore.location.hash.slice(1) : this.routerStore.location.hash,
                {}
            ),
        };
    }

    public getAttributeArrayFromHashParams(key: UrlKey, defaultValues: string[]): string[] {
        const values = this.hashParams[key] ? this.hashParams[key] : defaultValues;
        if (isGeneralArray(values)) {
            return values.filter((value) => value !== null) as string[];
        }
        throw Error('Cannot get string from getAttributeArrayFromUrl, use getAttributeFromUrl instead');
    }

    public getAttributeFromHashParams(key: UrlKey, defaultValue: string): string {
        const value = this.hashParams[key] ? this.hashParams[key] : defaultValue;
        if (!isGeneralArray(value) && value !== null) {
            return value;
        }
        throw Error('Cannot get string[] from getAttributeFromUrl, use getAttributeArrayFromUrl instead');
    }

    public listenToChannelChanges(onChannelChange: (channelName: string | null) => void): UnregisterCallback {
        return this.routerStore.history.listen((_) => {
            const hashParams = this.hashParams;

            onChannelChange(hashParams.channel);
        });
    }
}



// WEBPACK FOOTER //
// ./src/stores/UrlStore.ts